<template>
  <FormHeader :formStep="formStep" @onBackClick="onBack" />
  <FormContainer>
    <TransitionWrapper>
      <ToastMessage :notificationWidth="BREAKPOINTS.funnel_container_max_width.toString() + 'px'" />

      <FormTitle>{{ $t('agentRecommendation.title') }}</FormTitle>
      <FormContent>
        <div :class="$style.wrapper">
          <p :class="$style.subheading">
            {{ $t('agentRecommendation.description') }}
          </p>
          <div v-for="agent in agents" :key="agent.agentId" :class="$style.desktopAgentWrapper">
            <AgentCard :agent="agent" @selectAgent="onSelectAgent" />
          </div>
          <HgSlider
            :activeSlideIndex="activeSlideIndex"
            :visibleSlides="1"
            :gap="20"
            type="slide"
            :sliderComponents="agents"
            :class="$style.mobileAgentSlider"
            :peek="peekValues"
            :hideArrowsOnMobile="true"
            @slideChange="onSlideChange"
          >
            <template #default="sliderComponent">
              <AgentCard :agent="sliderComponent" @selectAgent="onSelectAgent" />
            </template>
          </HgSlider>
          <HgLink :class="$style.link" @click="onMoreAgentsLinkClick">
            {{ $t('agentRecommendation.moreAgentsLink') }}
          </HgLink>
          <FormBottom
            :onNext="onNext"
            :nextButtonLabel="nextButtonLabel"
            :nextDisabled="submissionInProgress"
          />
        </div>
      </FormContent>
      <HgModal v-model="openModal" mode="dialog" @close="openModal = false">
        <template #title>{{ $t('agentRecommendation.moreAgentsNotificationTitle') }}</template>
        <p :class="$style.tooltipContentDescription">
          {{ $t('agentRecommendation.moreAgentsNotification') }}
        </p>
        <template #footer>
          <HgButton theme="primary" @click="openModal = false">{{
            $t('agentRecommendation.moreAgentsNotificationCTA')
          }}</HgButton>
        </template>
      </HgModal>
    </TransitionWrapper>
  </FormContainer>
</template>

<script lang="ts" setup>
import {
  FormBottom,
  FormContainer,
  FormContent,
  FormHeader,
  FormTitle,
  ToastMessage,
  TransitionWrapper,
} from '@/components/common';
import useQueryParamTreatment, { ABnFeaturesQuery } from '@/composables/useQueryParamTreatment';
import { useResultNavigation } from '@/composables/useResultNavigation';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { getAgentRecommendationsForPostalCode } from '@/helpers/AgentRecommendations/agentRecommendations';
import { OfferType } from '@/models/valuationFunnel';
import {
  PP_3474_SplitFeatureFlags_Treatments,
  SplitFeatureFlags,
} from '@/splitExperiments/splitFeatureFlags';
import { usePropertyDetailsStore } from '@/stores/propertyDetails/propertyDetails';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { AgentRecommendation } from '@/stores/types/propertyValuation';
import {
  getStepInputs,
  trackingClickEvent,
  trackingFeatureDisplayEvent,
  trackingSellerLeadSubmit,
} from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { BREAKPOINTS, HttpStatusCode, ToastType, ValuationFlow } from '@/types';
import { HgButton, HgLink, HgModal, HgSlider } from '@smg-real-estate/domus';
import { useExperimentsStore } from '@smg-real-estate/openfeature-vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import AgentCard from './AgentCard.vue';

const { handleRoute } = useResultNavigation();
const propertyDetailsStore = usePropertyDetailsStore();
const propertyValuationStore = usePropertyValuationStore();
const {
  data: formData,
  loading: formLoading,
  step: formStep,
} = storeToRefs(propertyValuationStore);
const { t } = useI18n();

const openModal = ref(false);
const activeSlideIndex = ref(0);
const { locale } = useI18n();
const route = useRoute();
const experimentsStore = useExperimentsStore();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();
const queryTreatment = useQueryParamTreatment(ABnFeaturesQuery.PP_3474);
const redirectAfterSubmission = ref(false);
const submissionInProgress = computed(() => redirectAfterSubmission.value || formLoading.value);
const PP_3474_Treatment = computed(() => experimentsStore.getTreatment(SplitFeatureFlags.PP_3474));
const isAgentSelectionRequired = computed(
  () =>
    queryTreatment.value ===
      PP_3474_SplitFeatureFlags_Treatments.REQUIRED_SELECTION_AGENT_RECOMENDATIONS ||
    PP_3474_Treatment.value?.treatment ===
      PP_3474_SplitFeatureFlags_Treatments.REQUIRED_SELECTION_AGENT_RECOMENDATIONS,
);

const postalCode = computed(() => formData.value.address.postalCode);
const agents = computed(() => getAgentRecommendationsForPostalCode(postalCode.value) ?? []);
const getAgentId = (agent: AgentRecommendation) => agent.agentId;

const isAgentSelected = ref(
  agents.value.reduce<Record<string, boolean>>((agencySelections, agent) => {
    agencySelections[getAgentId(agent)] = true;
    return agencySelections;
  }, {}),
);

const nextButtonLabel = computed(() => {
  const selectedAgents = agents.value.filter((agent) => isAgentSelected.value[getAgentId(agent)]);
  if (!isAgentSelectionRequired.value && selectedAgents.length === 0)
    return t('agentRecommendation.continueWithNoBroker');
  return t('location.nextButtonLabel');
});

const peekValues = computed(() => {
  if (activeSlideIndex.value === agents.value.length - 1) {
    return { left: 50, right: 0 };
  } else return { left: 0, right: 50 };
});

const onSlideChange = (newIndex: number): void => {
  activeSlideIndex.value = newIndex;
};

const onSelectAgent = (agentData: AgentRecommendation, isSelected: boolean): void => {
  isAgentSelected.value[getAgentId(agentData)] = isSelected;
};

const onBack = (): void => {
  propertyValuationStore.navigateBack(ValuationFlow.AgentRecommendations);
};

const onMoreAgentsLinkClick = (): void => {
  trackingClickEvent(TRACKING_ACTION.MORE_AGENTS_LINK, TRACKING_LABEL.VALUATION_FUNNEL);
  openModal.value = true;
};

const getToastType = (statusCode: HttpStatusCode | undefined): ToastType => {
  if (statusCode && statusCode === HttpStatusCode.TooManyRequests) {
    return ToastType.TooManyRequests;
  } else {
    return ToastType.ServerFailure;
  }
};

const setToastType = (type: ToastType) => {
  propertyDetailsStore.setToastType(type);
};

const onNext = async (): Promise<void> => {
  const selectedAgents = agents.value.filter((agent) => isAgentSelected.value[getAgentId(agent)]);
  if (selectedAgents.length === 0 && isAgentSelectionRequired.value) {
    setToastType(ToastType.AgentSelectionRequired);
    trackingFeatureDisplayEvent('display_input_agents_error', 'valuation_funnel');
    return;
  }

  propertyValuationStore.setFormData({
    agentRecommendations: selectedAgents,
  });

  redirectAfterSubmission.value = true;

  const { isSuccess, result, statusCode } = await propertyValuationStore.submitQualifiedValuation();

  if (isSuccess && result) {
    propertyValuationStore.setFormData({
      leadId: result.leadId,
    });
    propertyValuationStore.setLeadFromQualifiedValuation(result);

    if (formData.value?.offerType === OfferType.Selling) {
      trackingSellerLeadSubmit(formData.value?.leadId || '', {
        h_event_label_008: selectedAgents.length,
      });
    }

    // Navigate directly to result page

    setToastType(ToastType.SubmitSuccess);
    await handleRoute(result.leadId);
  } else {
    redirectAfterSubmission.value = false;
    setToastType(getToastType(statusCode));
  }
};

onMounted(() => {
  trackVirtualPageViewWithTreatment({
    subType: 'input_agents',
    locale: locale.value,
    stepInputs: getStepInputs(
      {
        ...formData.value,
      },
      route.query.source?.toString() || '',
      formStep.value,
    ),
  });
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.wrapper {
  position: relative;
}

.subheading {
  @include body-2($color-grey-100);
}
.desktopAgentWrapper {
  display: none;
  padding-bottom: $space-150;
  @include grid-media-query(sm) {
    display: block;
  }
}

.mobileAgentSlider {
  display: block;
  padding-bottom: $space-150;
  @include grid-media-query(sm) {
    display: none;
  }

  :deep([class*='splide__list']) {
    align-items: stretch;
  }

  :deep([class*='splide__slide']) {
    display: flex;
    height: auto;
  }

  :deep([class*='splide__slide'] > *) {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
  }
}
.link {
  @include typography-label;
  padding: $space-100 0px;
  align-self: center;
  display: flex;
  justify-content: center;
}
</style>
